import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ─── DATA MODELS ─────────────────────────────────────────────────────────────

export interface POCompany {
  name: string;
  address: string;
  gstin: string;
  logoUrl?: string; // base64 or URL; leave empty to skip logo
}

export interface POSupplier {
  name: string;
  supplierCode: string;
  contactPerson: string;
  mobile: string;
  email: string;
  gstNumber: string;
  address: string;
}

export interface POProjectDetails {
  projectName: string;
  projectCode: string;
  prNo: string;
  department: string;
  deliverySchedule: string;
  paymentTerms: string;
  deliveryAddress: string;
  gstType?: string;
  sameState?: boolean;
}

export interface POItem {
  srNo: number;
  description: string;
  qty: number;
  unit: string;
  rate: number;
  discPercent: number;
  gstPercent: number;
  basicAmt: number;
  total: number;
}

export interface POCharge {
  srNo: number;
  description: string;
  gstPercent: number;
  amount: number;
  total: number;
}

export interface POSummary {
  materialSubTotal: number;
  totalDiscount: number;
  totalAdditionalCharges: number;
  totalGST: number;
  roundOff: number;
  grandTotal: number;
}

export interface CommercialTerm {
  heading: string;
  description: string;
}

export interface PurchaseOrderData {
  poNo: string;
  poDate: string;
  company: POCompany;
  supplier: POSupplier;
  project: POProjectDetails;
  items: POItem[];
  charges: POCharge[];
  summary: POSummary;
  amountInWords: string;
  preparedBy?: string;
  verifiedBy?: string;
  approvedBy?: string;
  commercialTerms?: CommercialTerm[];
  approvals?: POApproval[];
}

export interface POApproval {
  level: number;
  employeeName: string;
  designation: string;
  status: string;
  signature?: string; // base64 or url
}

// ─── SERVICE ─────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class PoPdfService {

  // ── PUBLIC ENTRY POINT ──────────────────────────────────────────────────────

  async generatePDF(data: PurchaseOrderData, printWindow?: Window | null): Promise<void> {
    const htmlString = this.buildHTML(data);

    // Render into a hidden off-screen container
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: -99999px;
      left: -99999px;
      width: 794px;
      background: #fff;
      z-index: -1;
    `;
    container.innerHTML = htmlString;
    document.body.appendChild(container);

    try {
      // Capture the full height of the rendered content as one high-res canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 794,
        height: container.scrollHeight,
        width: container.scrollWidth,
      });

      // Use px units so we can reason about element positions directly
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4', hotfixes: ['px_scaling'] });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();

      // Scale factor: canvas pixels (scale:2) → pdf px units
      const scale = pageW / canvas.width;

      // ── Find safe page-break points ─────────────────────────────────────────
      // Measure every top-level section so we never cut through one.
      // getBoundingClientRect() returns CSS pixels; multiply by 2 for canvas pixels.
      const containerRect = container.getBoundingClientRect();
      const sections = Array.from(
        container.querySelectorAll(
          '.header, .table-section, .charges-section, .payment-section, .lower-section, .footer'
        )
      ) as HTMLElement[];

      const sectionBounds = sections.map(el => {
        const r = el.getBoundingClientRect();
        return {
          top:    (r.top    - containerRect.top) * 2,   // canvas px
          bottom: (r.bottom - containerRect.top) * 2,   // canvas px
        };
      });

      // Walk through the canvas top-to-bottom, advancing by one page height at a time.
      // If the natural cut point lands inside a section, move the cut to just before
      // that section's top so the section starts fresh on the next page.
      const pageHeightCanvas = pageH / scale;  // one page in canvas px
      const totalCanvas = canvas.height;

      const cuts: number[] = [0];
      let cursor = pageHeightCanvas;

      while (cursor < totalCanvas) {
        const inside = sectionBounds.find(b => cursor > b.top + 4 && cursor < b.bottom - 4);
        if (inside) {
          // Snap cut to just before this section starts
          cursor = Math.max(cuts[cuts.length - 1] + 10, inside.top - 4);
        }
        cuts.push(cursor);
        cursor = cuts[cuts.length - 1] + pageHeightCanvas;
      }
      cuts.push(totalCanvas); // sentinel end

      // ── Render each slice onto its own PDF page ─────────────────────────────
      for (let i = 0; i < cuts.length - 1; i++) {
        if (i > 0) pdf.addPage();

        const sliceTop = cuts[i];
        const sliceH   = Math.min(cuts[i + 1], totalCanvas) - sliceTop;

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width  = canvas.width;
        sliceCanvas.height = sliceH;

        const ctx = sliceCanvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, sliceTop, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

        const imgData  = sliceCanvas.toDataURL('image/jpeg', 0.98);
        const slicePdfH = sliceH * scale;   // height of this slice in pdf px units
        pdf.addImage(imgData, 'JPEG', 0, 0, pageW, slicePdfH);
      }

      const blob    = pdf.output('blob');
      const blobUrl = URL.createObjectURL(blob);
     // const printWindow = window.open('', '_blank');

      if (printWindow) {
       printWindow.location.href = blobUrl;
      } 
      else {
        pdf.save(`PO-${data.poNo}.pdf`);
      }
    } finally {
      document.body.removeChild(container);
    }
  }

  // ── HTML BUILDER ────────────────────────────────────────────────────────────

  private buildHTML(d: PurchaseOrderData): string {
    const showDiscountColumn = (d.items ?? []).some(item => (item.discPercent ?? 0) > 0) || (d.summary?.totalDiscount ?? 0) > 0;
    const showGstColumn = String(d.project.gstType ?? '').trim().toLowerCase() !== 'without gst';
    const useIgst = d.project.sameState === false;
    const taxLabel = useIgst ? 'IGST%' : 'GST%';
    const summaryTaxLabel = useIgst ? 'Total IGST' : 'Total GST';
    return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<style>
*{margin:0;padding:0;box-sizing:border-box;font-family:'Segoe UI',Arial,sans-serif;}
body{background:#fff;padding:10px;color:#1e293b;}
.page{width:774px;background:#fff;padding:10px 40px;min-height:1123px;display:flex;flex-direction:column;position:relative;padding-bottom:220px;}

/* HEADER */
.header{border:1px solid #dce4ec;border-radius:8px;overflow:hidden;}
.top-header{background:#ffffff;color:#1e293b;padding:13px 15px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #dce4ec;}
.company-left{display:flex;align-items:center;gap:12px;}


/* FIX: logo box is a fixed square that never grows/shrinks with its flex siblings,
   and the logo image is painted as a CSS background (not an img tag) because
   html2canvas does not reliably honor object-fit on img elements - it was
   ignoring the aspect ratio and stretching the logo into an oval. background-size:
   contain is well supported by html2canvas and keeps the true aspect ratio. */
.logo{
  width:100px;height:100px;min-width:100px;min-height:100px;
  flex:0 0 100px;
  overflow:hidden;
  background-repeat:no-repeat;
  background-position:center center;
  background-size:contain;
}


.logo-placeholder{width:100px;height:100px;min-width:100px;min-height:100px;flex:0 0 100px;background:#334155;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:10px;color:#94a3b8;text-align:center;padding:4px;}




.company-details{font-size:10.5px;line-height:1.6;}
.company-details .uppercase{font-size:12px;font-weight:800;letter-spacing:0.5px;}
.supplier-name{font-size:13px;font-weight:700;text-transform:uppercase;}
/* FIX: po-right is now a fixed narrow column instead of growing with its content,
   which frees up horizontal space in the header row for the bigger logo. The
   PO NO / DATE badges stack vertically instead of sitting side by side, since
   two badges side-by-side needed ~230px of width - stacked they only need
   as much width as the wider single badge. */
.po-right{flex:0 0 150px;max-width:150px;text-align:right;}
.po-title{font-size:16px;font-weight:700;letter-spacing:0.3px;white-space:nowrap;line-height:1;}
.po-info{margin-top:6px;display:flex;flex-direction:column;align-items:flex-end;gap:5px;}
.po-badge{background:#2563eb;color:#ffffff;padding:4px 9px;border-radius:14px;font-size:9.5px;font-weight:800;letter-spacing:0.3px;white-space:nowrap;}

/* DETAILS */
.details-section{padding:10px 14px 8px;display:grid;grid-template-columns:1fr 1fr;gap:18px;}
.card-heading{display:flex;align-items:center;gap:8px;margin-bottom:8px;padding-bottom:4px;border-bottom:1px solid #dce4ec;}
.card-icon{width:24px;height:24px;border-radius:6px;background:#2563eb;color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;}
.card-heading h3{font-size:13px;color:#0f172a;letter-spacing:0.3px;}
.info-grid{display:grid;grid-template-columns:130px 1fr;row-gap:5px;column-gap:8px;align-items:start;}
.full-width{grid-column:1/-1;}
.label{font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.4px;}
.value{font-size:11px;font-weight:600;color:#111827;line-height:1.4;}

/* ITEM TABLE */
.table-section{margin-top:8px;page-break-inside:avoid;break-inside:avoid;}
table{width:100%;border-collapse:collapse;border:1px solid #dce4ec;border-radius:8px;overflow:hidden;}

.thead{background:#f1f5f9;color:#0f172a;}
.thead th{padding:8px 5px;font-size:10px;border:1px solid #dce4ec;text-transform:uppercase;font-weight:700;color:#475569;}

tbody td{padding:7px 5px;border:1px solid #dce4ec;font-size:10.5px;}
tbody tr{page-break-inside:avoid;break-inside:avoid;}
tbody tr:nth-child(even){background:#f8fafc;}
.tc{text-align:center;}
.tr{text-align:right;}

/* CHARGES */
.charges-section{margin-top:10px;page-break-inside:avoid;break-inside:avoid;}
.section-title{background:#f1f5f9;border:1px solid #dce4ec;border-bottom:none;padding:8px 12px;font-size:12px;font-weight:700;color:#0f172a;border-radius:8px 8px 0 0;}
.charges-table{width:100%;border-collapse:collapse;}
.charges-table thead{background:#f1f5f9;color:#0f172a;}
.charges-table thead th{padding:7px 5px;font-size:10px;border:1px solid #dce4ec;text-transform:uppercase;}
.charges-table tbody td{padding:7px 5px;border:1px solid #dce4ec;font-size:10.5px;}
.charges-table tbody tr{page-break-inside:avoid;break-inside:avoid;}
.charges-table tbody tr:nth-child(even){background:#f8fafc;}

/* PAYMENT TERMS */
.payment-section{margin-top:10px;border:1px solid #dce4ec;border-radius:8px;overflow:hidden;page-break-inside:avoid;break-inside:avoid;}
.payment-section .box-title{background:#f1f5f9;padding:8px 12px;font-size:12px;font-weight:700;color:#0f172a;border-bottom:1px solid #dce4ec;}
.payment-section .payment-content{padding:10px 14px;}
.payment-section .card-heading{margin-bottom:0;border-bottom:none;padding-bottom:0;}

/* LOWER */
.lower-section{margin-top:10px;display:grid;grid-template-columns:1.35fr 0.65fr;gap:12px;align-items:stretch;page-break-inside:avoid;break-inside:avoid;}
.terms-box{border:1px solid #dce4ec;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;page-break-inside:avoid;break-inside:avoid;}
.box-title{background:#f1f5f9;padding:8px 12px;font-size:12px;font-weight:700;color:#0f172a;border-bottom:1px solid #dce4ec;}
.terms-content{padding:10px 12px;flex:1;}
.term-grid{display:grid;grid-template-columns:1fr 1fr;gap:7px 18px;}
.term{position:relative;padding-left:14px;font-size:10px;line-height:1.55;color:#334155;}
.term::before{content:"•";position:absolute;left:0;top:0;color:#2563eb;font-size:16px;line-height:1;}
.amount-words{margin-top:10px;background:#f8fbff;border-left:3px solid #2563eb;padding:8px 10px;font-size:10.5px;font-weight:700;line-height:1.5;}
.summary-box{border:1px solid #dce4ec;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;height:100%;page-break-inside:avoid;break-inside:avoid;}
.summary-content{padding:0;flex:1;display:flex;flex-direction:column;justify-content:space-between;}
.summary-row{display:flex;justify-content:space-between;padding:8px 12px;border-bottom:1px solid #edf2f7;font-size:10.5px;}
.summary-row:last-child{border-bottom:none;}
.summary-row span:first-child{color:#475569;font-weight:600;}
.summary-row span:last-child{font-weight:700;}
.grand-total{background:#eff6ff;color:#0f172a;font-size:12px;font-weight:800;}

/* FOOTER */
.footer{position:absolute;bottom:30px;left:10px;right:10px;padding-top:20px;page-break-inside:avoid;break-inside:avoid;page-break-before:avoid;break-before:avoid;}

/* FIX: signatures now arrange themselves dynamically regardless of how many
   approvals exist (1, 2, 3, 4, 5+). A flex-wrap row with fixed-width items and
   centered justification means: 1 signature sits centered, 2 sit centered as a
   pair, 3 fill a row evenly, 4+ wrap onto additional centered rows — instead of
   the old fixed 3-column grid which left ragged gaps or an orphaned lone item. */
.signature-grid{
  display:flex;
  flex-wrap:wrap;
  justify-content:center;
  align-items:flex-end;
  gap:20px 40px;
  margin-top:30px;
}

.signature{
  flex:0 0 190px;
  max-width:190px;
  display:flex;
  flex-direction:column;
  justify-content:flex-end;
  align-items:center;
  text-align:center;
}

.signature img{
  height:50px;
  max-width:100%;
  object-fit:contain;
  margin-bottom:6px;
}

.signature-line{
  width:100%;
  border-top:1px solid #1e293b;
  margin-bottom:5px;
}

.signature p{font-size:10.5px;font-weight:700;line-height:1.5;}
</style>
</head>
<body>
<div class="page">

  <!-- HEADER -->
  <div class="header">
    <div class="top-header">
      <div class="company-left">


        ${d.company.logoUrl
          ? `<div class="logo" style="background-image:url('${d.company.logoUrl}');"></div>`
          : `<div class="logo-placeholder">YOUR<br>LOGO</div>`}



        <div class="company-details">
          <strong class="uppercase">${this.esc(d.company.name)}</strong><br>
          ${this.esc(d.company.address)}<br>
          GSTIN : ${this.esc(d.company.gstin)}
        </div>
      </div>
      <div class="po-right">
        <div class="po-title">PURCHASE ORDER</div>
        <div class="po-info">
          <div class="po-badge">PO NO : ${this.esc(d.poNo)}</div>
          <div class="po-badge">DATE : ${this.esc(d.poDate)}</div>
        </div>
      </div>
    </div>

    <!-- DETAILS SECTION -->
    <div class="details-section">

      <!-- SUPPLIER -->
      <div class="details-card">
        <div class="card-heading">
          <div class="card-icon">S</div>
          <h3>Supplier Details</h3>
        </div>
        <div class="info-grid">
          <div class="value full-width supplier-name uppercase">${this.esc(d.supplier.name)}</div>
          <div class="value full-width">${this.esc(d.supplier.address)}</div>
          <div class="label">GST Number</div>
          <div class="value">${this.esc(d.supplier.gstNumber)}</div>
          <div class="label full-width">Contact Details</div>
          <div class="value full-width"></div>
          <div class="label">Mobile</div>
          <div class="value">${this.esc(d.supplier.mobile)}</div>
          <div class="label">Email</div>        
          <div class="value">${this.esc(d.supplier.email)}</div>
        </div>
      </div>

      <!-- PROJECT -->
      <div class="details-card">
        <div class="card-heading">
          <div class="card-icon">P</div>
          <h3>Project &amp; Delivery Details</h3>
        </div>
        <div class="info-grid">
          <div class="label">Project Name</div>       <div class="value uppercase">${this.esc(d.project.projectName)}</div>
          <div class="label">Project Code</div>       <div class="value">${this.esc(d.project.projectCode)}</div>
          <div class="label">PR No</div>              <div class="value">${this.esc(d.project.prNo)}</div>
          <div class="label">Department</div>         <div class="value">${this.esc(d.project.department)}</div>
          <div class="label">Delivery Schedule</div>  <div class="value">${this.esc(d.project.deliverySchedule)}</div>
          <div class="label">Delivery Address</div>   <div class="value">${this.esc(d.project.deliveryAddress)}</div>
          <div class="label">GST Type</div>           <div class="value">${this.esc(d.project.gstType ?? '-')}</div>
        </div>
      </div>

    </div>
  </div>

  <!-- ITEM TABLE -->
  <div class="table-section">
    <table>
      <thead class="thead">
        <tr>
          <th width="4%">#</th>
          <th width="25%">Description</th>
          <th width="7%">Qty</th>
          <th width="7%">Unit</th>
          <th width="10%">Rate</th>
          ${showDiscountColumn ? `<th width="8%">Disc%</th>` : ''}
          ${showGstColumn ? `<th width="8%">${taxLabel}</th>` : ''}
          <th width="12%">Basic Amt</th>
          <th width="19%">Total</th>
        </tr>
      </thead>
      <tbody>
        ${d.items.map(item => `
        <tr>
          <td class="tc">${item.srNo}</td>
          <td>${this.esc(item.description)}</td>
          <td class="tc">${item.qty}</td>
          <td class="tc">${this.esc(item.unit)}</td>
          <td class="tr">${this.fmt(item.rate)}</td>
          ${showDiscountColumn ? `<td class="tc">${item.discPercent}%</td>` : ''}
          ${showGstColumn ? `<td class="tc">${item.gstPercent}%</td>` : ''}
          <td class="tr">${this.fmt(item.basicAmt)}</td>
          <td class="tr">${this.fmt(item.total)}</td>
        </tr>`).join('')}
      </tbody>
    </table>
  </div>

  ${Array.isArray(d.charges) && d.charges.length > 0 ? `
  <!-- ADDITIONAL CHARGES -->
  <div class="charges-section">
    <div class="section-title">Additional Charges Details</div>
    <table class="charges-table">
      <thead>
        <tr>
          <th width="6%">#</th>
          <th width="44%">Charge Description</th>
          ${showGstColumn ? `<th width="15%">GST %</th>` : ''}
          <th width="15%">Amount</th>
          <th width="20%">Total</th>
        </tr>
      </thead>
      <tbody>
        ${d.charges.map(c => `
        <tr>
          <td class="tc">${c.srNo}</td>
          <td>${this.esc(c.description)}</td>
          ${showGstColumn ? `<td class="tc">${c.gstPercent}%</td>` : ''}
          <td class="tr">${this.fmt(c.amount)}</td>
          <td class="tr">${this.fmt(c.total)}</td>
        </tr>`).join('')}
      </tbody>
    </table>
  </div>
  ` : ''}

  <!-- PAYMENT TERMS (moved below additional charges) -->
  <div class="payment-section">
    <div class="box-title">Payment Terms</div>
    <div class="payment-content">
      <div class="info-grid">
        <div class="label">Payment Terms</div>
        <div class="value">${this.esc(d.project.paymentTerms)}</div>
      </div>
    </div>
  </div>

  <!-- LOWER SECTION -->
  <div class="lower-section">

    <div>
      <!-- TERMS -->
      ${Array.isArray(d.commercialTerms) && d.commercialTerms.length > 0 ? `
      <div class="terms-box">
        <div class="box-title">Commercial Terms &amp; Conditions</div>
        <div class="terms-content">
          <div class="term-grid">
            ${d.commercialTerms.map((term, index) => `
            <div class="term"><strong>${this.esc(term.heading || `Term ${index + 1}`)}:</strong> ${this.esc(term.description || '')}</div>
            `).join('')}
          </div>
        </div>
      </div>
      ` : ''}

      <!-- AMOUNT IN WORDS -->
      <div class="amount-words">
        Amount In Words : ${this.esc(d.amountInWords)}
      </div>
    </div>

    <!-- SUMMARY -->
    <div class="summary-box">
      <div class="box-title">Order Summary</div>
      <div class="summary-content">
        <div class="summary-row"><span>Material Sub Total</span><span>₹ ${this.fmt(d.summary.materialSubTotal)}</span></div>
        ${showDiscountColumn ? `<div class="summary-row"><span>Total Discount</span><span>₹ ${this.fmt(d.summary.totalDiscount)}</span></div>` : ''}
        ${Array.isArray(d.charges) && d.charges.length > 0 ? `
        <div class="summary-row"><span>Total Additional Charges</span><span>₹ ${this.fmt(d.summary.totalAdditionalCharges)}</span></div>
        ` : ''}
        ${showGstColumn ? `<div class="summary-row"><span>${summaryTaxLabel}</span><span>₹ ${this.fmt(d.summary.totalGST)}</span></div>` : ''}
        <div class="summary-row"><span>Round Off</span><span>₹ ${this.fmt(d.summary.roundOff)}</span></div>
        <div class="summary-row grand-total"><span>Grand Total</span><span>₹ ${this.fmt(d.summary.grandTotal)}</span></div>
      </div>
    </div>

  </div>

  <!-- FOOTER / SIGNATURES -->
  <div class="footer">



    <div class="signature-grid">
  ${(d.approvals ?? []).map(a => `
    <div class="signature">

      ${
        a.signature
          ? `<img src="${a.signature}" style="height:50px; margin-bottom:5px;" />`
          : `<div style="height:50px;"></div>`
      }

      <div class="signature-line"></div>

      <p>
        ${this.esc(a.employeeName || '')}<br>
        ${this.esc(a.designation || '')}<br>
      </p>

    </div>
  `).join('')}
</div>


  </div>

</div>
</body>
</html>`;
  }

  // ── HELPERS ─────────────────────────────────────────────────────────────────

  /** Escape HTML special characters to prevent XSS inside the generated HTML */
  private esc(val: string | undefined | null): string {
    return (val ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Format number to Indian comma style with 2 decimal places */

  private fmt(val?: number | null): string {
  return Number(val ?? 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

async generatePdfBlob(data: PurchaseOrderData): Promise<Blob> {
    const htmlString = this.buildHTML(data);

    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: -99999px;
      left: -99999px;
      width: 794px;
      background: #fff;
      z-index: -1;
    `;

    container.innerHTML = htmlString;
    document.body.appendChild(container);

    try {

        const canvas = await html2canvas(container, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            windowWidth: 794,
            height: container.scrollHeight,
            width: container.scrollWidth,
        });

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'px',
            format: 'a4',
            hotfixes: ['px_scaling']
        });

       const pageW = pdf.internal.pageSize.getWidth();
const pageH = pdf.internal.pageSize.getHeight();

// Scale factor: canvas pixels (scale:2) → pdf px units
const scale = pageW / canvas.width;

// Find safe page-break points
const containerRect = container.getBoundingClientRect();
const sections = Array.from(
  container.querySelectorAll(
    '.header, .table-section, .charges-section, .payment-section, .lower-section, .footer'
  )
) as HTMLElement[];

const sectionBounds = sections.map(el => {
  const r = el.getBoundingClientRect();
  return {
    top: (r.top - containerRect.top) * 2,
    bottom: (r.bottom - containerRect.top) * 2,
  };
});

const pageHeightCanvas = pageH / scale;
const totalCanvas = canvas.height;

const cuts: number[] = [0];
let cursor = pageHeightCanvas;

while (cursor < totalCanvas) {
  const inside = sectionBounds.find(
    b => cursor > b.top + 4 && cursor < b.bottom - 4
  );

  if (inside) {
    cursor = Math.max(cuts[cuts.length - 1] + 10, inside.top - 4);
  }

  cuts.push(cursor);
  cursor = cuts[cuts.length - 1] + pageHeightCanvas;
}

cuts.push(totalCanvas);

// Render pages
for (let i = 0; i < cuts.length - 1; i++) {

  if (i > 0) {
    pdf.addPage();
  }

  const sliceTop = cuts[i];
  const sliceH = Math.min(cuts[i + 1], totalCanvas) - sliceTop;

  const sliceCanvas = document.createElement('canvas');
  sliceCanvas.width = canvas.width;
  sliceCanvas.height = sliceH;

  const ctx = sliceCanvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);

  ctx.drawImage(
    canvas,
    0,
    sliceTop,
    canvas.width,
    sliceH,
    0,
    0,
    canvas.width,
    sliceH
  );

  const imgData = sliceCanvas.toDataURL('image/jpeg', 0.98);
  const slicePdfH = sliceH * scale;

  pdf.addImage(imgData, 'JPEG', 0, 0, pageW, slicePdfH);
}

return pdf.output('blob');

    } finally {
        document.body.removeChild(container);
    }
}






}